/**
 * Market Intelligence – Competitor Prices Card (Ratemetrics CURRENT snapshot)
 * Pilcore PMS – Revenue Management
 * Autor: Alberto Castro Ull
 */

import React, { useEffect, useMemo, useState } from "react";
import axios from "../api/axiosInstance";
import ApplyScrapedPricesPopup from "./ApplyScrapedPricesPopup";

export default function CompetitorPricesCard({ businessUnitId: propBusinessUnitId = null }) {
  const currentUser = JSON.parse(localStorage.getItem("palantir_user") || "null");
  const businessUnitId = propBusinessUnitId || currentUser?.active_business_unit_id || null;

  const [showApplyPopup, setShowApplyPopup] = useState(false);

  const [config, setConfig] = useState(null);
  const [rows, setRows] = useState([]);
  const [dates, setDates] = useState([]);
  const [page, setPage] = useState(0);
  const daysPerPage = 7;
  const [loading, setLoading] = useState(true);

  // ✅ Filters (multi-select)
  const [selectedMealPlans, setSelectedMealPlans] = useState([]); // global
  const [selectedRatePlans, setSelectedRatePlans] = useState([]); // global (values = getRatePlanKey)

  const [hotelMealPlans, setHotelMealPlans] = useState({}); // { [hotel_name]: string[] }
  const [hotelRatePlans, setHotelRatePlans] = useState({}); // { [hotel_name]: string[] }

  // ✅ NEW: mapping competitor room type -> my room category name
const [rmRoomCategories, setRmRoomCategories] = useState([]);
const [rmMappings, setRmMappings] = useState([]);

useEffect(() => {
  const run = async () => {
    if (!businessUnitId) return;

    try {
      const [catsRes, mapsRes] = await Promise.all([
        axios.get("/api/room_categories", {
          params: { business_unit_id: Number(businessUnitId) },
        }),
        axios.get("/api/ratemetrics/competitor-room-type-mappings"),
      ]);

      setRmRoomCategories(Array.isArray(catsRes.data) ? catsRes.data : []);
      setRmMappings(Array.isArray(mapsRes.data) ? mapsRes.data : []);
    } catch (e) {
      console.error("[RATEMETRICS][UI] failed to load mappings/categories", e?.response?.data || e);
      setRmRoomCategories([]);
      setRmMappings([]);
    }
  };

  run();
}, [businessUnitId]);

const normalizeKey = (v) => String(v || "").trim().toLowerCase().replace(/\s+/g, " ");

const myCategoryNameById = useMemo(() => {
  const m = new Map();
  for (const c of rmRoomCategories || []) {
    const id = Number(c.id);
    const name = String(c.name || "").trim();
    if (id && name) m.set(id, name);
  }
  return m;
}, [rmRoomCategories]);

// key: `${hotel_code}|${normalized_room_type}` -> "My Category Name"
const assignedMyCategoryByCompetitorRoomType = useMemo(() => {
  const out = new Map(); // key -> Set(names)

  for (const mp of rmMappings || []) {
    const h = Number(mp.competitor_hotel_code);
    const catId = Number(mp.room_category_id);
    const catName = myCategoryNameById.get(catId) || null;
    if (!h || !catName) continue;

    const raw = normalizeKey(mp.competitor_room_type_raw);
    const norm = normalizeKey(mp.competitor_room_type_norm);

    const keys = [];
    if (raw) keys.push(`${h}|${raw}`);
    if (norm) keys.push(`${h}|${norm}`);

    for (const k of keys) {
      if (!out.has(k)) out.set(k, new Set());
      out.get(k).add(catName);
    }
  }

  // Set -> string
  const finalMap = new Map();
  for (const [k, set] of out.entries()) {
    finalMap.set(k, Array.from(set).sort().join(" / "));
  }
  return finalMap;
}, [rmMappings, myCategoryNameById]);


  const [filterModal, setFilterModal] = useState({
    isOpen: false,
    scope: "global", // "global" | "hotel"
    hotel_name: null,
    kind: "meal", // "meal" | "rate"
  });

  // ✅ NEW: selected competitor series (1 per row: hotel_code + room_type)
  const [selectedSeriesKeys, setSelectedSeriesKeys] = useState([]);

  const getMealPlanKey = (r) => {
    const v = String(r?.meal_plan || "").trim();
    return v.length ? v : "(none)";
  };

  const getRatePlanKey = (r) => {
    const code = r?.rate_plan_code;
    if (code !== null && code !== undefined && String(code).trim() !== "") return `code:${String(code)}`;
    const txt = String(r?.rate_plan || "").trim();
    return txt.length ? `text:${txt}` : "text:(none)";
  };

  const buildMealPlanOptions = (list) => {
    const set = new Set();
    for (const r of list || []) set.add(getMealPlanKey(r));
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  };

  const buildRatePlanOptions = (list) => {
    const map = new Map(); // key -> label
    for (const r of list || []) {
      const key = getRatePlanKey(r);
      if (map.has(key)) continue;

      const code = r?.rate_plan_code;
      const txt = String(r?.rate_plan || "").trim() || "(none)";
      const label =
        code !== null && code !== undefined && String(code).trim() !== ""
          ? `${String(code)} — ${txt}`
          : txt;

      map.set(key, label);
    }

    return Array.from(map.entries())
      .sort((a, b) => String(a[1]).localeCompare(String(b[1])))
      .map(([value, label]) => ({ value, label }));
  };

  // Global options are based on ALL rows (so you can always expand),
  // but hotel modals will use options derived from globalFilteredRows.
  const globalMealPlanOptions = useMemo(() => buildMealPlanOptions(rows || []), [rows]);
  const globalRatePlanOptions = useMemo(() => buildRatePlanOptions(rows || []), [rows]);

  const ratePlanLabelByValue = useMemo(() => {
    const m = new Map();
    for (const opt of globalRatePlanOptions) m.set(opt.value, opt.label);
    return m;
  }, [globalRatePlanOptions]);

  // ✅ Global-filtered rows (hotel filters are applied later per hotel group)
  const globalFilteredRows = useMemo(() => {
    return (rows || []).filter((r) => {
      const mp = getMealPlanKey(r);
      const rp = getRatePlanKey(r);

      if (selectedMealPlans.length > 0 && !selectedMealPlans.includes(mp)) return false;
      if (selectedRatePlans.length > 0 && !selectedRatePlans.includes(rp)) return false;

      return true;
    });
  }, [rows, selectedMealPlans, selectedRatePlans]);

  useEffect(() => {
    setPage(0);
  }, [selectedMealPlans, selectedRatePlans]);

  // ✅ If global filters change, clamp hotel filters to global selections (intersection)
  useEffect(() => {
    setHotelMealPlans((prev) => {
      const out = { ...prev };
      for (const k of Object.keys(out)) {
        if (selectedMealPlans.length === 0) continue;
        out[k] = (out[k] || []).filter((v) => selectedMealPlans.includes(v));
      }
      return out;
    });

    setHotelRatePlans((prev) => {
      const out = { ...prev };
      for (const k of Object.keys(out)) {
        if (selectedRatePlans.length === 0) continue;
        out[k] = (out[k] || []).filter((v) => selectedRatePlans.includes(v));
      }
      return out;
    });
  }, [selectedMealPlans, selectedRatePlans]);

  // Load BU config (to map hotel_code -> hotel name)
  useEffect(() => {
    const run = async () => {
      if (!businessUnitId) return;
      try {
        const res = await axios.get("/api/ratemetrics/rateshop-configs", {
          params: { business_unit_id: Number(businessUnitId) },
        });
        const list = Array.isArray(res.data) ? res.data : [];
        setConfig(list[0] || null);
      } catch (e) {
        console.error("[RATEMETRICS][UI] failed to load config", e?.response?.data || e);
        setConfig(null);
      }
    };
    run();
  }, [businessUnitId]);

  // Load current competitor prices (already filtered by scope in backend)
  useEffect(() => {
    const run = async () => {
      if (!businessUnitId) return;
      setLoading(true);
      try {
        const res = await axios.get("/api/ratemetrics/competitor-prices/current", {
          params: { business_unit_id: Number(businessUnitId) },
        });
        const list = Array.isArray(res.data) ? res.data : [];
        setRows(list);

        const dateSet = new Set();
        for (const r of list) {
          const d = String(r?.check_in || "").slice(0, 10);
          if (d.length === 10) dateSet.add(d);
        }
        const sorted = Array.from(dateSet).sort();
        setDates(sorted);
        setPage(0);
      } catch (e) {
        console.error("[RATEMETRICS][UI] failed to load current prices", e?.response?.data || e);
        setRows([]);
        setDates([]);
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [businessUnitId]);

  const hotelNameByCode = useMemo(() => {
    const m = new Map();
    const comps = Array.isArray(config?.competitor_hotels) ? config.competitor_hotels : [];
    for (const h of comps) {
      const code = Number(h?.hotel_code);
      const name = String(h?.name || "").trim();
      if (code && name) m.set(code, name);
    }
    return m;
  }, [config]);

  const handleApply = async (payload) => {
  try {
    await axios.post(
      `${process.env.REACT_APP_API_URL}/api/scraper-targets/apply-scraped`,
      { ...payload, business_unit_id: Number(businessUnitId) }
    );

    alert("✅ Market prices applied successfully.");
    setShowApplyPopup(false);
  } catch (err) {
    console.error("❌ Error applying market prices:", err?.response?.data || err);
    alert("Failed to apply market prices.");
  }
};


  const openSourceUrl = (url) => {
    const u = String(url || "").trim();
    if (!u) return;
    console.log("🔗 [RATEMETRICS][UI] opening source_url:", u);
    window.open(u, "_blank", "noopener,noreferrer");
  };

  const getSeriesKey = (hotel_code, room_type) => {
    const hotelCodeKey =
      hotel_code === null || hotel_code === undefined || String(hotel_code).trim() === ""
        ? "x"
        : String(Number(hotel_code));
    const roomTypeDisplay = String(room_type || "—").trim() || "—";
    const roomTypeKey = roomTypeDisplay.toLowerCase();
    return `rm:${hotelCodeKey}:${roomTypeKey}`;
  };

  // Build grouped matrix: Hotel -> RoomType rows (for the table)
  const grouped = useMemo(() => {
    const map = new Map(); // hotel_codeKey -> {hotel_code, hotel_name, roomsMap}

    for (const r of globalFilteredRows || []) {
      const hotel_code = Number(r?.hotel_code);
      const hotel_name = hotelNameByCode.get(hotel_code) || (hotel_code ? `Hotel ${hotel_code}` : "Hotel");

      // ✅ per-hotel filters (already clamped by global filters)
      const mpSel = hotelMealPlans[hotel_name] || [];
      const rpSel = hotelRatePlans[hotel_name] || [];

      const mealKey = getMealPlanKey(r);
      const planKey = getRatePlanKey(r);

      if (mpSel.length > 0 && !mpSel.includes(mealKey)) continue;
      if (rpSel.length > 0 && !rpSel.includes(planKey)) continue;

      const room_type = String(r?.room_type || "—").trim() || "—";
      const date = String(r?.check_in || "").slice(0, 10);
      if (date.length !== 10) continue;

      const hotelKey = hotel_code ? String(hotel_code) : `name:${hotel_name}`;
      if (!map.has(hotelKey)) map.set(hotelKey, { hotel_code: hotel_code || null, hotel_name, roomsMap: new Map() });

      const group = map.get(hotelKey);
      const compKey = `${Number(hotel_code)}|${normalizeKey(room_type)}`;
        const myCatLabel = assignedMyCategoryByCompetitorRoomType.get(compKey) || null;

        if (!group.roomsMap.has(room_type)) {
          group.roomsMap.set(room_type, { room_type, my_category: myCatLabel, prices: {}, urls: {}, metaByDate: {} });
        } else {
          // si ya existe, no machacamos; pero si no tenía label y ahora sí, se lo ponemos
          const existing = group.roomsMap.get(room_type);
          if (!existing.my_category && myCatLabel) existing.my_category = myCatLabel;
          if (!existing.metaByDate) existing.metaByDate = {};
        }

      const onsite = r?.onsite_rate_nightly ?? r?.onsite_rate;
      const priceNum = onsite == null ? null : Number(onsite);
      const price = Number.isFinite(priceNum) ? Number(priceNum.toFixed(2)) : null;

      const urlRaw = String(r?.source_url || "").trim();
      const url = urlRaw.length ? urlRaw : null;

      const curr = group.roomsMap.get(room_type).prices[date];
      if (price == null) continue;

      // ✅ meta del precio candidato (para tooltip del precio mostrado)
      const meta = {
        meal_plan: r?.meal_plan ?? null,
        rate_plan: r?.rate_plan ?? null,
        rate_plan_code: r?.rate_plan_code ?? null,
      };

      if (curr == null || price < curr) {
        group.roomsMap.get(room_type).prices[date] = price;
        group.roomsMap.get(room_type).urls[date] = url;
        group.roomsMap.get(room_type).metaByDate[date] = meta; // ✅ guardamos el ganador
      } else if (curr != null && group.roomsMap.get(room_type).urls[date] == null && url) {
        group.roomsMap.get(room_type).urls[date] = url;

        // ✅ si por lo que sea faltaba meta para ese día, lo rellenamos
        if (!group.roomsMap.get(room_type).metaByDate[date]) {
          group.roomsMap.get(room_type).metaByDate[date] = meta;
        }
      }

    }

    const out = [];
    for (const g of map.values()) {
      const roomsArr = Array.from(g.roomsMap.values()).sort((a, b) => String(a.room_type).localeCompare(String(b.room_type)));
      out.push({ hotel_code: g.hotel_code, hotel_name: g.hotel_name, rooms: roomsArr });
    }

    out.sort((a, b) => String(a.hotel_name).localeCompare(String(b.hotel_name)));
    return out;
  }, [globalFilteredRows, hotelNameByCode, hotelMealPlans, hotelRatePlans, assignedMyCategoryByCompetitorRoomType]);

  // ✅ Series to apply (1 per visible row: hotel_code + room_type)
  // - pricesByDate MIN per date (like the table)
  // - items keeps all variants so backend can re-query properly
  const tableSeriesSources = useMemo(() => {
    const map = new Map(); // seriesKey -> source

    for (const r of globalFilteredRows || []) {
      const hotel_code = Number(r?.hotel_code);
      const hotel_name = hotelNameByCode.get(hotel_code) || (hotel_code ? `Hotel ${hotel_code}` : "Hotel");

      // per-hotel filters
      const mpSel = hotelMealPlans[hotel_name] || [];
      const rpSel = hotelRatePlans[hotel_name] || [];

      const mealKey = getMealPlanKey(r);
      const planKey = getRatePlanKey(r);

      if (mpSel.length > 0 && !mpSel.includes(mealKey)) continue;
      if (rpSel.length > 0 && !rpSel.includes(planKey)) continue;

      const room_type = String(r?.room_type || "—").trim() || "—";
      const key = getSeriesKey(hotel_code, room_type);

      let s = map.get(key);
      if (!s) {
        s = {
          key,
          kind: "ratemetrics",
          label: `${hotel_name} — ${room_type}`,
          hotel_code: hotel_code || null,
          hotel_name,
          room_type,
          pricesByDate: {},
          items: [],
        };
        map.set(key, s);
      }

      // keep MIN per date
      const date = String(r?.check_in || "").slice(0, 10);
      if (date.length !== 10) continue;

      const onsite = r?.onsite_rate_nightly ?? r?.onsite_rate;
      const priceNum = onsite == null ? null : Number(onsite);
      const price = Number.isFinite(priceNum) ? Number(priceNum.toFixed(2)) : null;

      if (price != null) {
        const prev = s.pricesByDate[date];
        if (prev == null || price < prev) s.pricesByDate[date] = price;
      }

      // store variants
      s.items.push({
        hotel_code: hotel_code || null,
        hotel_name,
        room_type,
        website_code: r?.website_code ?? null,
        occupancy: r?.occupancy ?? null,
        los: r?.los ?? null,
        meal_plan: r?.meal_plan ?? null,
        rate_plan: r?.rate_plan ?? null,
        rate_plan_code: r?.rate_plan_code ?? null,
      });
    }

    const out = Array.from(map.values());
    out.sort((a, b) => String(a.label).localeCompare(String(b.label)));
    return out;
  }, [globalFilteredRows, hotelNameByCode, hotelMealPlans, hotelRatePlans, assignedMyCategoryByCompetitorRoomType]);

  // ✅ keep selectedSeriesKeys only if still visible under current filters
  useEffect(() => {
    const available = new Set((tableSeriesSources || []).map((s) => s.key));
    setSelectedSeriesKeys((prev) => (prev || []).filter((k) => available.has(k)));
  }, [tableSeriesSources]);

  const toggleSeriesKey = (key) => {
    setSelectedSeriesKeys((prev) => {
      const curr = Array.isArray(prev) ? prev : [];
      if (curr.includes(key)) return curr.filter((x) => x !== key);
      return [...curr, key];
    });
  };

  const visibleDates = dates.slice(page * daysPerPage, (page + 1) * daysPerPage);

  if (!businessUnitId) return null;
  if (!config && !loading) return null; // BU sin config => no mostramos nada
  if (loading) return null;

  const hasData = grouped.length > 0 && visibleDates.length > 0;

  const rowStyle = { height: "44px", minHeight: "44px" };
  const textCellStyle = {
    whiteSpace: "normal",
    wordWrap: "break-word",
    maxWidth: "320px",
    minWidth: "220px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    lineHeight: "1.15em",
    verticalAlign: "middle",
    paddingTop: "6px",
    paddingBottom: "6px",
    paddingLeft: "10px",
    paddingRight: "10px",
  };

  return (
    <div className="text-light" style={{ padding: 0, background: "transparent" }}>
      <div className="d-flex justify-content-between align-items-start mb-2" style={{ gap: "12px" }}>
        <div className="d-flex flex-column" style={{ gap: "6px", minWidth: "420px" }}>
          <h5 style={{ color: "#f0f0f0", fontSize: "1.0rem", fontWeight: 650, margin: 0 }}>
            Competitor Prices
          </h5>

          {/* Global filters */}
          <div className="d-flex align-items-center" style={{ gap: "10px", flexWrap: "wrap" }}>
            <button
              type="button"
              onClick={() => setFilterModal({ isOpen: true, scope: "global", hotel_name: null, kind: "meal" })}
              className="btn btn-sm btn-outline-light"
              style={{ padding: "3px 10px", fontSize: "0.78rem" }}
            >
              Meal plan
            </button>

            <div className="d-flex align-items-center" style={{ gap: "6px", flexWrap: "wrap" }}>
              {(selectedMealPlans || []).map((v) => (
                <span
                  key={`gmp-${v}`}
                  className="badge"
                  style={{
                    background: "rgba(255,255,255,0.10)",
                    color: "rgba(255,255,255,0.90)",
                    border: "1px solid rgba(255,255,255,0.14)",
                    fontSize: "0.72rem",
                    fontWeight: 600,
                    padding: "5px 8px",
                    borderRadius: "999px",
                    display: "inline-flex",
                    gap: "8px",
                    alignItems: "center",
                  }}
                >
                  {v}
                  <button
                    type="button"
                    onClick={() => setSelectedMealPlans((prev) => (prev || []).filter((x) => x !== v))}
                    style={{
                      border: "none",
                      background: "transparent",
                      color: "#ff5b5b",
                      fontSize: "0.95rem",
                      fontWeight: 900,
                      padding: 0,
                      cursor: "pointer",
                      lineHeight: "1",
                    }}
                    aria-label="Remove"
                    title="Remove"
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>

            <button
              type="button"
              onClick={() => setFilterModal({ isOpen: true, scope: "global", hotel_name: null, kind: "rate" })}
              className="btn btn-sm btn-outline-light"
              style={{ padding: "3px 10px", fontSize: "0.78rem" }}
            >
              Rate plan
            </button>

            <div className="d-flex align-items-center" style={{ gap: "6px", flexWrap: "wrap" }}>
              {(selectedRatePlans || []).map((v) => (
                <span
                  key={`grp-${v}`}
                  className="badge"
                  style={{
                    background: "rgba(48,216,255,0.10)",
                    color: "rgba(255,255,255,0.90)",
                    border: "1px solid rgba(48,216,255,0.25)",
                    fontSize: "0.72rem",
                    fontWeight: 600,
                    padding: "5px 8px",
                    borderRadius: "999px",
                    display: "inline-flex",
                    gap: "8px",
                    alignItems: "center",
                  }}
                >
                  {ratePlanLabelByValue.get(v) || v}
                  <button
                    type="button"
                    onClick={() => setSelectedRatePlans((prev) => (prev || []).filter((x) => x !== v))}
                    style={{
                      border: "none",
                      background: "transparent",
                      color: "#ff5b5b",
                      fontSize: "0.95rem",
                      fontWeight: 900,
                      padding: 0,
                      cursor: "pointer",
                      lineHeight: "1",
                    }}
                    aria-label="Remove"
                    title="Remove"
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Right side: Apply + paging */}
        <div className="d-flex flex-column align-items-end" style={{ gap: "8px" }}>
          <button
            type="button"
            onClick={() => {
              if ((selectedSeriesKeys || []).length === 0) {
                alert("Please select at least one competitor series in the table.");
                return;
              }
              setShowApplyPopup(true);
            }}
            style={{
              border: "1px solid rgba(48,216,255,0.9)",
              color: "#30d8ff",
              backgroundColor: "transparent",
              padding: "3px 10px",
              borderRadius: "4px",
              fontSize: "0.78rem",
              fontWeight: 600,
              cursor: "pointer",
              width: "fit-content",
            }}
          >
            Apply Market Price Reference (selected: {(selectedSeriesKeys || []).length})
          </button>

          {visibleDates.length > 0 && (
            <div className="d-flex align-items-center gap-2" style={{ paddingTop: "2px" }}>
              <button
                className="btn btn-sm btn-outline-light"
                style={{ padding: "3px 10px", fontSize: "0.78rem" }}
                disabled={page === 0}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
              >
                Prev
              </button>

              <span className="text-white-50" style={{ fontSize: "0.78rem" }}>
                {visibleDates[0]} → {visibleDates[visibleDates.length - 1]}
              </span>

              <button
                className="btn btn-sm btn-outline-light"
                style={{ padding: "3px 10px", fontSize: "0.78rem" }}
                disabled={(page + 1) * daysPerPage >= dates.length}
                onClick={() => setPage((p) => ((p + 1) * daysPerPage < dates.length ? p + 1 : p))}
              >
                Next
              </button>
            </div>
          )}
        </div>
      </div>

      {!hasData ? (
        <p className="text-muted mb-0">No competitor prices available for this Business Unit.</p>
      ) : (
        <div
          className="table-responsive"
          style={{
            maxHeight: "calc(100vh - 280px)",
            overflowY: "auto",
            overflowX: "auto",
            border: "1px solid #333",
            borderRadius: "6px",
            paddingBottom: "12px",
          }}
        >
          <table
            className="table table-dark table-sm text-center align-middle mb-0"
            style={{
              minWidth: "1160px",
              fontSize: "0.78rem",
              lineHeight: "1.0rem",
              whiteSpace: "nowrap",
            }}
          >
            <thead>
              <tr>
                <th
                  style={{
                    position: "sticky",
                    top: 0,
                    background: "#151515",
                    zIndex: 6,
                    width: "52px",
                  }}
                >
                  Sel
                </th>

                <th
                  style={{
                    position: "sticky",
                    top: 0,
                    background: "#151515",
                    zIndex: 6,
                    whiteSpace: "nowrap",
                    width: "260px",
                    textAlign: "left",
                    paddingLeft: "10px",
                  }}
                >
                  Room type
                </th>

                {visibleDates.map((d) => (
                  <th
                    key={d}
                    style={{
                      position: "sticky",
                      top: 0,
                      background: "#151515",
                      zIndex: 6,
                      whiteSpace: "nowrap",
                      width: "105px",
                    }}
                  >
                    {d}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {grouped.map((hotelGroup) => (
                <React.Fragment key={`${hotelGroup.hotel_name}|||${hotelGroup.hotel_code || "x"}`}>
                  {/* HOTEL header row */}
                  <tr>
                    <td
                      colSpan={2 + visibleDates.length}
                      style={{
                        background: "linear-gradient(to right, rgba(26,188,156,0.18), rgba(52,152,219,0.12))",
                        borderTop: "1px solid rgba(255,255,255,0.08)",
                        borderBottom: "1px solid rgba(255,255,255,0.08)",
                        textAlign: "left",
                        padding: "7px 10px",
                        fontWeight: 750,
                        letterSpacing: "0.2px",
                      }}
                    >
                      <div className="d-flex justify-content-between align-items-center" style={{ gap: "12px" }}>
                        <div style={{ fontWeight: 750 }}>{hotelGroup.hotel_name}</div>

                        {/* per-hotel filters + chips (unchanged layout you already had) */}
                        <div className="d-flex align-items-center" style={{ gap: "8px", flexWrap: "wrap" }}>
                          <button
                            type="button"
                            onClick={() =>
                              setFilterModal({
                                isOpen: true,
                                scope: "hotel",
                                hotel_name: hotelGroup.hotel_name,
                                kind: "meal",
                              })
                            }
                            className="btn btn-sm btn-outline-light"
                            style={{ padding: "2px 8px", fontSize: "0.74rem" }}
                          >
                            Meal plan
                          </button>

                          {(hotelMealPlans[hotelGroup.hotel_name] || []).map((v) => (
                            <span
                              key={`hmp-${hotelGroup.hotel_name}-${v}`}
                              className="badge"
                              style={{
                                background: "rgba(255,255,255,0.10)",
                                color: "rgba(255,255,255,0.90)",
                                border: "1px solid rgba(255,255,255,0.14)",
                                fontSize: "0.72rem",
                                fontWeight: 650,
                                padding: "5px 8px",
                                borderRadius: "999px",
                                display: "inline-flex",
                                gap: "8px",
                                alignItems: "center",
                              }}
                            >
                              {v}
                              <button
                                type="button"
                                onClick={() =>
                                  setHotelMealPlans((prev) => ({
                                    ...prev,
                                    [hotelGroup.hotel_name]: (prev?.[hotelGroup.hotel_name] || []).filter((x) => x !== v),
                                  }))
                                }
                                style={{
                                  border: "none",
                                  background: "transparent",
                                  color: "#ff5b5b",
                                  fontSize: "0.95rem",
                                  fontWeight: 900,
                                  padding: 0,
                                  cursor: "pointer",
                                  lineHeight: "1",
                                }}
                                aria-label="Remove"
                                title="Remove"
                              >
                                ×
                              </button>
                            </span>
                          ))}

                          <button
                            type="button"
                            onClick={() =>
                              setFilterModal({
                                isOpen: true,
                                scope: "hotel",
                                hotel_name: hotelGroup.hotel_name,
                                kind: "rate",
                              })
                            }
                            className="btn btn-sm btn-outline-light"
                            style={{ padding: "2px 8px", fontSize: "0.74rem" }}
                          >
                            Rate plan
                          </button>

                          {(hotelRatePlans[hotelGroup.hotel_name] || []).map((v) => (
                            <span
                              key={`hrp-${hotelGroup.hotel_name}-${v}`}
                              className="badge"
                              style={{
                                background: "rgba(48,216,255,0.10)",
                                color: "rgba(255,255,255,0.90)",
                                border: "1px solid rgba(48,216,255,0.25)",
                                fontSize: "0.72rem",
                                fontWeight: 650,
                                padding: "5px 8px",
                                borderRadius: "999px",
                                display: "inline-flex",
                                gap: "8px",
                                alignItems: "center",
                              }}
                            >
                              {ratePlanLabelByValue.get(v) || v}
                              <button
                                type="button"
                                onClick={() =>
                                  setHotelRatePlans((prev) => ({
                                    ...prev,
                                    [hotelGroup.hotel_name]: (prev?.[hotelGroup.hotel_name] || []).filter((x) => x !== v),
                                  }))
                                }
                                style={{
                                  border: "none",
                                  background: "transparent",
                                  color: "#ff5b5b",
                                  fontSize: "0.95rem",
                                  fontWeight: 900,
                                  padding: 0,
                                  cursor: "pointer",
                                  lineHeight: "1",
                                }}
                                aria-label="Remove"
                                title="Remove"
                              >
                                ×
                              </button>
                            </span>
                          ))}
                        </div>
                      </div>
                    </td>
                  </tr>

                  {hotelGroup.rooms.map((r, idx) => {
                    const seriesKey = getSeriesKey(hotelGroup.hotel_code, r.room_type);
                    const checked = (selectedSeriesKeys || []).includes(seriesKey);

                    return (
                      <tr key={`${hotelGroup.hotel_name}|||${r.room_type}|||${idx}`} style={rowStyle}>
                        <td style={{ width: "52px" }}>
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleSeriesKey(seriesKey)}
                            style={{ cursor: "pointer" }}
                            title="Select this competitor series"
                          />
                        </td>

                        <td style={{ ...textCellStyle, textAlign: "left" }}>
                          <span>{r.room_type}</span>
                          {r.my_category ? (
                            <span style={{ marginLeft: 8, fontStyle: "italic", color: "rgba(255,255,255,0.45)" }}>
                              assigned to: {r.my_category}
                            </span>
                          ) : null}
                        </td>


                                                {visibleDates.map((d) => {
                          const v = r.prices?.[d];
                          const url = r.urls?.[d];
                          const meta = r.metaByDate?.[d] || null;

                          const clickable = v != null && url;

                          const mealLabel = (() => {
                            const s = String(meta?.meal_plan || "").trim();
                            return s.length ? s : "(none)";
                          })();

                          const rateLabel = (() => {
                            const code = meta?.rate_plan_code;
                            const txt = String(meta?.rate_plan || "").trim() || "(none)";
                            const hasCode = code !== null && code !== undefined && String(code).trim() !== "";
                            return hasCode ? `${String(code)} — ${txt}` : txt;
                          })();

                          const tooltip =
                            v == null
                              ? ""
                              : `Meal plan: ${mealLabel}\nRate plan: ${rateLabel}${clickable ? "\n(click to open source)" : ""}`;

                          return (
                            <td
                              key={d}
                              onClick={() => {
                                if (!clickable) return;
                                openSourceUrl(url);
                              }}
                              style={{
                                textAlign: "center",
                                minWidth: "105px",
                                paddingTop: "6px",
                                paddingBottom: "6px",
                                height: "44px",
                                verticalAlign: "middle",
                                color: v == null ? "rgba(255,255,255,0.35)" : "rgba(255,255,255,0.92)",
                                cursor: clickable ? "pointer" : "default",
                                textDecoration: "none",
                              }}
                              title={tooltip}
                            >
                              {v == null ? "—" : `${v.toFixed(2)}€`}
                            </td>
                          );
                        })}

                      </tr>
                    );
                  })}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showApplyPopup && (
        <ApplyScrapedPricesPopup
          isOpen={showApplyPopup}
          sources={tableSeriesSources}
          businessUnitId={businessUnitId}
          onClose={() => setShowApplyPopup(false)}
          onApply={handleApply}
          hideSeriesSelector={true}
          initialSelectedKeys={selectedSeriesKeys}
        />
      )}

      {filterModal.isOpen && (
        <MultiSelectFilterPopup
          isOpen={filterModal.isOpen}
          title={
            filterModal.scope === "global"
              ? `Global ${filterModal.kind === "meal" ? "Meal plan" : "Rate plan"} filter`
              : `${filterModal.hotel_name} — ${filterModal.kind === "meal" ? "Meal plan" : "Rate plan"} filter`
          }
          options={
            filterModal.kind === "meal"
              ? buildMealPlanOptions(
                  filterModal.scope === "global"
                    ? (rows || [])
                    : (globalFilteredRows || []).filter((r) => {
                        const hotel_code = Number(r?.hotel_code);
                        const hotel_name = hotelNameByCode.get(hotel_code) || (hotel_code ? `Hotel ${hotel_code}` : "Hotel");
                        return hotel_name === filterModal.hotel_name;
                      })
                ).map((v) => ({ value: v, label: v }))
              : buildRatePlanOptions(
                  filterModal.scope === "global"
                    ? (rows || [])
                    : (globalFilteredRows || []).filter((r) => {
                        const hotel_code = Number(r?.hotel_code);
                        const hotel_name = hotelNameByCode.get(hotel_code) || (hotel_code ? `Hotel ${hotel_code}` : "Hotel");
                        return hotel_name === filterModal.hotel_name;
                      })
                )
          }
          selectedValues={
            filterModal.scope === "global"
              ? (filterModal.kind === "meal" ? selectedMealPlans : selectedRatePlans)
              : (filterModal.kind === "meal"
                  ? (hotelMealPlans[filterModal.hotel_name] || [])
                  : (hotelRatePlans[filterModal.hotel_name] || []))
          }
          onClose={() => setFilterModal({ isOpen: false, scope: "global", hotel_name: null, kind: "meal" })}
          onApply={(vals) => {
            const clean = Array.isArray(vals) ? vals : [];

            if (filterModal.scope === "global") {
              if (filterModal.kind === "meal") setSelectedMealPlans(clean);
              else setSelectedRatePlans(clean);
            } else {
              const h = filterModal.hotel_name;
              if (filterModal.kind === "meal") setHotelMealPlans((prev) => ({ ...prev, [h]: clean }));
              else setHotelRatePlans((prev) => ({ ...prev, [h]: clean }));
            }

            setFilterModal({ isOpen: false, scope: "global", hotel_name: null, kind: "meal" });
          }}
        />
      )}
    </div>
  );

  function MultiSelectFilterPopup({ isOpen, title, options, selectedValues, onClose, onApply }) {
    const [contentReady, setContentReady] = useState(false);
    const [local, setLocal] = useState([]);

    useEffect(() => {
      if (!isOpen) return;
      setContentReady(false);

      async function loadEverything() {
        setLocal(Array.isArray(selectedValues) ? [...selectedValues] : []);
        setContentReady(true);
      }

      loadEverything();
    }, [isOpen, selectedValues]);

    const filtered = useMemo(() => {
      return Array.isArray(options) ? options : [];
    }, [options]);

    if (!isOpen) return null;
    if (!contentReady) return null;

    return (
      <div
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 9999,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "rgba(0,0,0,0.55)",
          backdropFilter: "blur(6px)",
          WebkitBackdropFilter: "blur(6px)",
          padding: "18px",
        }}
        onClick={onClose}
      >
        <div
          style={{
            width: "92vw",
            maxWidth: "820px",
            background: "#121212",
            border: "1px solid rgba(255,255,255,0.12)",
            borderRadius: "10px",
            boxShadow: "0 18px 60px rgba(0,0,0,0.60)",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="p-3 text-light">
            <div className="d-flex justify-content-between align-items-center" style={{ gap: "12px" }}>
              <div style={{ fontSize: "1.0rem", fontWeight: 700 }}>{title}</div>
              <button type="button" className="btn btn-sm btn-outline-light" onClick={onClose}>
                Close
              </button>
            </div>

            <div className="mt-3 d-flex align-items-center" style={{ gap: "10px", flexWrap: "wrap" }}>
              <div className="text-white-50" style={{ fontSize: "0.78rem" }}>
                Selected: {local.length}
              </div>
            </div>

            <div
              className="mt-3"
              style={{
                maxHeight: "52vh",
                overflowY: "auto",
                border: "1px solid rgba(255,255,255,0.12)",
                borderRadius: "8px",
              }}
            >
              <div className="p-2">
                {filtered.map((o) => {
                  const v = o.value;
                  const checked = local.includes(v);
                  return (
                    <label
                      key={v}
                      className="d-flex align-items-center"
                      style={{
                        gap: "10px",
                        padding: "8px 10px",
                        borderRadius: "6px",
                        cursor: "pointer",
                        userSelect: "none",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => {
                          setLocal((prev) => {
                            const curr = Array.isArray(prev) ? prev : [];
                            if (curr.includes(v)) return curr.filter((x) => x !== v);
                            return [...curr, v];
                          });
                        }}
                      />
                      <div style={{ fontSize: "0.85rem" }}>{o.label}</div>
                    </label>
                  );
                })}
                {filtered.length === 0 && (
                  <div className="text-white-50 p-2" style={{ fontSize: "0.85rem" }}>
                    No matches.
                  </div>
                )}
              </div>
            </div>

            <div className="d-flex justify-content-end mt-3" style={{ gap: "10px" }}>
              <button type="button" className="btn btn-sm btn-outline-light" onClick={onClose}>
                Cancel
              </button>
              <button type="button" className="btn btn-sm btn-primary" onClick={() => onApply(local)}>
                Apply
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }
}
